<template>
	<v-card class="mx-auto" elevation="0">
		<v-card-title v-if="!wizardMode" class="card-header fixed-card-title2">
			{{ $t('addNewHospitalTooltip') }}
			<v-spacer />
			<v-icon @click="closeDrawer" class="white-icon">mdi-close</v-icon>
		</v-card-title>

		<v-card-text class="pt-4" :class="{ 'pb-2': wizardMode }" :style="wizardMode ? 'height: 100%' : ''">
			<v-form ref="form" lazy-validation>
				<v-container>
					<v-row>
						<v-col class="d-flex pa-1" cols="12">
							<v-text-field
								outlined
								dense
								hide-details="auto"
								v-model="hospitalToAdd.hospitalName"
								:rules="[(v) => !!v || $t('required-field')]"
								:label="$t('location-name')"
								:key="'textField' + componentKey"
								required />
						</v-col>
					</v-row>
					<v-row>
						<v-col class="d-flex pa-1" cols="12">
							<v-select
								class="width-text-field2"
								hide-details
								dense
								v-model="hospitalToAdd.medicalInstitutionId"
								:items="allMedicalInstitutions"
								item-value="id"
								item-text="institutionName"
								:label="$t('medicalInstitution')"
								:key="'select' + componentKey"
								outlined
								:menu-props="{ offsetY: true }" />
						</v-col>
					</v-row>
					<v-row>
						<v-col class="d-flex pa-1" cols="12">
							<v-textarea
								outlined
								dense
								no-resize
								hide-details="auto"
								v-model="hospitalToAdd.description"
								:label="$t('description')"
								:key="'textArea' + componentKey"
								required
								:rules="[(v) => !!v || $t('required-field')]" />
						</v-col>
					</v-row>
					<v-row>
						<v-col class="d-flex pa-1" cols="12">
							<div class="pt-3">
								<h5 class="heading text-left">{{ this.$t('address') }}</h5>
							</div>
						</v-col>
					</v-row>
					<v-row>
						<v-col class="d-flex pa-1" cols="12">
							<v-text-field
								outlined
								hide-details="auto"
								dense
								v-model="hospitalToAdd.address"
								:label="$t('address')"
								:rules="[(v) => !!v || $t('required-field')]"
								:key="'textField1' + componentKey"
								required />
						</v-col>
					</v-row>
					<v-row>
						<v-col class="d-flex pa-1" cols="6">
							<v-text-field
								disabled
								outlined
								hide-details="auto"
								dense
								v-model="hospitalToAdd.city"
								:label="$t('city')" />
						</v-col>
						<v-col class="d-flex pa-1" cols="6">
							<v-text-field
								disabled
								outlined
								hide-details="auto"
								dense
								v-model="hospitalToAdd.state"
								:label="$t('state')" />
						</v-col>
					</v-row>
					<v-row>
						<v-col class="d-flex pa-1" cols="6">
							<v-text-field
								disabled
								outlined
								hide-details="auto"
								dense
								v-model="hospitalToAdd.zip"
								:label="$t('zip')" />
						</v-col>
						<v-col class="d-flex pa-1" cols="6">
							<v-text-field
								disabled
								outlined
								hide-details="auto"
								dense
								v-model="hospitalToAdd.country"
								:label="$t('country')" />
						</v-col>
					</v-row>
					<v-row>
						<v-col class="d-flex pa-1" cols="12">
							<div class="pt-3">
								<h5 class="heading text-left">{{ this.$t('contact') }}</h5>
							</div>
						</v-col>
					</v-row>
					<v-row dense class="pt-2 mx-n3">
						<v-col cols="12">
							<v-text-field
								outlined
								hide-details="auto"
								dense
								v-model="hospitalToAdd.contactPerson"
								:rules="[(v) => !!v || $t('required-field')]"
								:label="$t('contact-person')"
								required
								:key="'textField7' + componentKey" />
						</v-col>
						<v-col cols="6">
							<v-text-field
								outlined
								hide-details="auto"
								dense
								v-model="hospitalToAdd.contactPhone"
								:rules="[(v) => !!v || $t('required-field'), (v) => v?.length > 10 || $t('invalid-phone-number')]"
								:label="$t('contact-phone')"
								@input="formatPhoneNumber"
								required
								:key="'textField8' + componentKey" />
						</v-col>
						<v-col cols="6">
							<v-text-field
								outlined
								hide-details="auto"
								dense
								v-model="hospitalToAdd.contactEmail"
								:rules="[(v) => !!v || $t('required-field')]"
								:label="$t('contact-email')"
								required
								:key="'textField9' + componentKey" />
						</v-col>
					</v-row>
					<v-row dense class="mx-n3">
						<v-col cols="12">
							<v-checkbox class="ma-0" hide-details v-model="dailyReminder" :label="$t('daily-reminder')" />
						</v-col>
						<v-col cols="12">
							<v-checkbox
								class="ma-0"
								hide-details
								v-model="dailyMeasurementsReportDone"
								:label="$t('daily-measurements-report-done')" />
						</v-col>
					</v-row>
				</v-container>
			</v-form>
		</v-card-text>
		<v-card-actions v-if="!wizardMode" class="fixed-card-actions px-4">
			<v-row dense>
				<v-col cols="6">
					<v-btn elevation="0" @click="closeDrawer" class="cancel-button-style capitalize-text">
						{{ $t('cancel') }}
					</v-btn>
				</v-col>
				<v-col cols="6">
					<v-btn :disabled="isSaveButtonDisabled()" @click="saveHospital" class="save-button-style">
						{{ $t('save') }}
					</v-btn>
				</v-col>
			</v-row>
		</v-card-actions>
	</v-card>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import Hospital from '../../data/models/Hospital';
import { showSuccessAlert, showErrorAlert } from '@/utils/utilities';
import { AsYouType } from 'libphonenumber-js';

export default {
	name: 'HopsitalAddDrawer',
	props: { wizardMode: { type: Boolean, default: false }, dataProps: { type: Object, default: null, required: false } },
	data: () => ({
		componentKey: 0,
		menu: false,
		hospitalToAdd: new Hospital(),
		defaultTimeZone: 'America/Chicago',
		isValidForWizard: false,
	}),
	computed: {
		...mapState({
			allMedicalInstitutions: (state) => state.medicalInstitutions.medicalInstitutions,
			hospitals: (state) => state.hospitals.hospitals,
		}),
		...mapGetters({
			getTimeZone: 'verifiers/getTimeZone',
		}),
		dailyReminder: {
			get () {
				return JSON.parse(this.hospitalToAdd.notificationSettings)?.dailyReportNotification.dailyReminder || false;
			},
			set (value) {
				const updateObject = {
					dailyReportNotification: {
						dailyReminder: value || false,
						daylyMeasurementsReportDone:
							JSON.parse(this.hospitalToAdd.notificationSettings)?.dailyReportNotification
								.daylyMeasurementsReportDone || false,
					},
				};
				this.hospitalToAdd.notificationSettings = JSON.stringify(updateObject);
			},
		},
		dailyMeasurementsReportDone: {
			get () {
				return (
					JSON.parse(this.hospitalToAdd.notificationSettings)?.dailyReportNotification.daylyMeasurementsReportDone ||
					false
				);
			},
			set (value) {
				const updateObject = {
					dailyReportNotification: {
						dailyReminder:
							JSON.parse(this.hospitalToAdd.notificationSettings)?.dailyReportNotification.dailyReminder || false,
						daylyMeasurementsReportDone: value || false,
					},
				};
				this.hospitalToAdd.notificationSettings = JSON.stringify(updateObject);
			},
		},
	},
	async mounted () {
		if (this.dataProps) {
			this.hospitalToAdd.medicalInstitutionId = this.dataProps.pccCompaniesMapping[0].mappedCompanyId;
			this.hospitalToAdd.hospitalName = this.dataProps.facilityName;
			this.hospitalToAdd.description = this.dataProps.pccCompaniesMapping[0].description;
			this.hospitalToAdd.city = this.dataProps.city;
			this.hospitalToAdd.state = this.dataProps.state;
			this.hospitalToAdd.zip = this.dataProps.postalCode;
			this.hospitalToAdd.country = this.dataProps.country;
			this.hospitalToAdd.address =
				this.dataProps.addressLine1 +
				', ' +
				this.dataProps.city +
				', ' +
				this.dataProps.state +
				' ' +
				this.dataProps.postalCode +
				', ' +
				this.dataProps.country;
			this.hospitalToAdd.contactPhone = this.dataProps.phone;
			this.hospitalToAdd.contactPerson = '-';
			this.hospitalToAdd.contactEmail = this.dataProps.email || '-';
		} else {
			await this.getMedicalInstitutions();
			if (this.allMedicalInstitutions.length === 1) {
				this.hospitalToAdd.medicalInstitutionId = this.allMedicalInstitutions[0].id;
			}
		}
	},

	watch: {
		// Add a watcher on hospitalToAdd to check form validity whenever data changes
		hospitalToAdd: {
			deep: true,
			handler () {
				this.checkFormValidity();
			},
		},
	},

	methods: {
		...mapActions({
			getMedicalInstitutions: 'medicalInstitutions/getMedicalInstitutions',
		}),
		isSaveButtonDisabled () {
			return (
				!this.hospitalToAdd.hospitalName ||
				!this.hospitalToAdd.medicalInstitutionId ||
				!this.hospitalToAdd.description ||
				!this.hospitalToAdd.address ||
				!this.hospitalToAdd.contactPerson ||
				!(this.hospitalToAdd.contactPhone?.length > 10) ||
				!this.hospitalToAdd.contactEmail
			);
		},

		checkFormValidity () {
			// We invert the result of isSaveButtonDisabled
			const newValidity = !this.isSaveButtonDisabled();

			// Only emit if the status has actually changed to avoid excessive updates
			if (this.isValid !== newValidity) {
				this.isValid = newValidity;
				// Emit the current validity status to the parent
				this.$emit('update:wizard-valid', newValidity);
			}
		},

		formatPhoneNumber (val) {
			this.hospitalToAdd.contactPhone = new AsYouType().input(val);
		},
		resetComponent () {
			// Reset firstname or any other component state as needed
			this.hospitalToAdd.hospitalName = '';
			this.hospitalToAdd.medicalInstitutionId = '';
			this.hospitalToAdd.description = '';
			this.hospitalToAdd.address = '';
			this.hospitalToAdd.city = '';
			this.hospitalToAdd.state = '';
			this.hospitalToAdd.zip = '';
			this.hospitalToAdd.country = '';
			this.hospitalToAdd.contactPerson = '';
			this.hospitalToAdd.contactEmail = '';
			this.hospitalToAdd.contactPhone = '+1';
			// Change the key to trigger component reset
			this.componentKey += 1;
		},
		async getHospitals () {
			await this.$store.dispatch('hospitals/getHospitals').then(() => {
				this.closeDrawer();
			});
		},
		async saveHospital () {
			await this.checkAndFixLocationDetails();

			const body = {
				medicalInstitutionId: this.hospitalToAdd.medicalInstitutionId,
				hospitalName: this.hospitalToAdd.hospitalName,
				description: this.hospitalToAdd.description,
				contactPerson: this.hospitalToAdd.contactPerson,
				contactPhone: this.hospitalToAdd.contactPhone,
				contactEmail: this.hospitalToAdd.contactEmail,
				address: this.hospitalToAdd.address,
				zip: this.hospitalToAdd.zip,
				city: this.hospitalToAdd.city,
				state: this.hospitalToAdd.state,
				country: this.hospitalToAdd.country,
				addressLatitude: this.hospitalToAdd.addressLatitude,
				addressLongitude: this.hospitalToAdd.addressLongitude,
				hospitalSpetializationId: this.hospitalToAdd.hospitalSpetializationId,
				hospitalTimeZone: this.hospitalToAdd.hospitalTimeZone,
				notificationSettings: this.hospitalToAdd.notificationSettings,
			};
			this.$store.dispatch('hospitals/createHospital', body).then((res) => {
				if (res) {
					showSuccessAlert(this.$t('successfully-created-organization'));
					this.getHospitals(); // reload the list of hospitals
					this.$emit('update:createdFacility', res.data);
				} else {
					showErrorAlert(this.$t('failed-operation'));
				}
			});
		},
		save (date) {
			this.$refs.menu.save(date);
		},
		closeDrawer () {
			this.hospitalToAdd = new Hospital();
			this.resetComponent();
			this.$store.commit('SET_DETAILS_DRAWER', false);
		},

		async checkAndFixLocationDetails () {
			try {
				const h = this.hospitalToAdd;

				if (h.address) {
					const addressForValidation = `${h.address}`;
					const geoLocation = await this.$store.dispatch('googleAPI/getGeoLocation', addressForValidation);

					if (!geoLocation || !geoLocation.address_components || !geoLocation.geometry) {
						h.addressLatitude = 0.01;
						h.addressLongitude = 0.01;
						return false;
					}

					const components = geoLocation.address_components;
					const getComponent = (type) => {
						const found = components.find((c) => c.types.includes(type));
						return found ? found.long_name : 'N/A';
					};

					// Extract and set location info
					const lat = geoLocation.geometry.location.lat || 0.01;
					const lng = geoLocation.geometry.location.lng || 0.01;

					h.addressLatitude = lat;
					h.addressLongitude = lng;
					h.address = geoLocation.formatted_address || h.address;
					h.city =
						getComponent('locality') ||
						getComponent('sublocality') ||
						getComponent('administrative_area_level_2') ||
						'N/A';
					h.state = getComponent('administrative_area_level_1') || 'N/A';
					h.country = getComponent('country') || 'N/A';
					h.zip = getComponent('postal_code') || 'N/A';

					// Now call Time Zone API
					const timestamp = Math.floor(Date.now() / 1000); // current time in seconds
					const timeZoneResponse = await fetch(
						`https://maps.googleapis.com/maps/api/timezone/json?location=${lat},${lng}&timestamp=${timestamp}&key=${process.env.VUE_APP_GOOGLEMAPAPIKEY}`,
					);
					const timeZoneData = await timeZoneResponse.json();

					if (timeZoneData.status === 'OK') {
						h.timeZoneId = timeZoneData.timeZoneId; // e.g., "Europe/Skopje"
						h.timeZoneName = timeZoneData.timeZoneName; // e.g., "Central European Summer Time"
						h.rawOffset = timeZoneData.rawOffset; // base offset in seconds
						h.dstOffset = timeZoneData.dstOffset; // daylight savings offset in seconds
						h.hospitalTimeZone = timeZoneData.timeZoneId;
					} else {
						// If timezone fetch failed, set default or null
						h.timeZoneId = 'N/A';
						h.timeZoneName = 'N/A';
						h.rawOffset = 0;
						h.dstOffset = 0;
						h.hospitalTimeZone = this.defaultTimeZone;
					}

					return true;
				} else {
					h.addressLatitude = 0.01;
					h.addressLongitude = 0.01;
					return false;
				}
			} catch (error) {
				this.hospitalToAdd.addressLatitude = 0.01;
				this.hospitalToAdd.addressLongitude = 0.01;
				this.hospitalToAdd.hospitalTimeZone = this.defaultTimeZone;
				return false;
			}
		},
	},
};
</script>
<style scoped>
.map-container {
	margin-top: 20px;
	/* Adjust this margin as needed */
}
</style>
